import React from 'react';
import {useTranslation} from 'react-i18next';
import {InfoRow} from '../InfoRow';
import {formatStopNumber} from '../../utils/stopNumberUtils';
import {formatTimestampToSecondsTime} from '../../utils/dateTimeUtils';
import {maskPhoneNumber} from '../../utils/phoneUtils';
import OrderNoteItem from '../OrderNoteItem/OrderNoteItem';

export interface PackageBasicInfoProps {
    packageDetail: {
        trackingNumber: string;
        deliveryTaskOrder?: {
            stopNumber?: number | null;
            subStopNumber?: string | null;
            routeName?: string | null;
            orderNote?: {
                id: number;
                note: string;
                lang: string;
                orderNoteTranslations: Array<{
                    lang: string;
                    translateNote: string;
                }>;
            } | null;
        } | null;
        deliverAt?: number | null;
        assignedDriver?: string | null;
        taskDate?: string | null;
        deliveryAttempts?: number;
        recipientName?: string | null;
        recipientPhone?: string | null;
        recipientAddress?: string | null;
    };
    currentLanguage: string;
    showOrderInfoTitle?: boolean;
    showCustomerInfoTitle?: boolean;
    additionalOrderFields?: React.ReactNode;
    additionalCustomerFields?: React.ReactNode;
}

export const PackageBasicInfo: React.FC<PackageBasicInfoProps> = ({
    packageDetail,
    currentLanguage,
    showOrderInfoTitle = true,
    showCustomerInfoTitle = true,
    additionalOrderFields,
    additionalCustomerFields
}) => {
    const {t} = useTranslation();

    return (
        <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
            <div className="mb-1">
                {showOrderInfoTitle && (
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.orderInfo')}</h2>
                )}
                <InfoRow
                    label={t('pages.dsp.packageDetail.trackingNo')}
                    value={packageDetail.trackingNumber}
                />
                <InfoRow
                    label={t('pages.dsp.packageDetail.stop')}
                    value={packageDetail?.deliveryTaskOrder?.stopNumber != null
                        ? formatStopNumber(packageDetail.deliveryTaskOrder.stopNumber, packageDetail.deliveryTaskOrder.subStopNumber)
                        : '-'}
                />
                <InfoRow
                    label={t('pages.dsp.packageDetail.route')}
                    value={packageDetail?.deliveryTaskOrder?.routeName || '-'}
                />
                <InfoRow
                    label={t('pages.dsp.packageDetail.assignedTime')}
                    value={packageDetail?.deliverAt ? formatTimestampToSecondsTime(packageDetail.deliverAt) : '-'}
                />
                <InfoRow
                    label={t('pages.dsp.packageDetail.deliveryAttempt')}
                    value={`${packageDetail?.deliveryAttempts || 0} out of 3`}
                />
                <InfoRow
                    label={t('pages.dsp.packageDetail.driver')}
                    value={packageDetail?.assignedDriver || '-'}
                />
                <InfoRow
                    label={t('pages.dsp.packageDetail.taskDate')}
                    value={packageDetail?.taskDate || '-'}
                />
                <OrderNoteItem
                    orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                    currentLanguage={currentLanguage}
                />
                {additionalOrderFields}

                {showCustomerInfoTitle && (
                    <h2 className="text-base font-medium mt-4">{t('pages.dsp.packageDetail.customerInfo')}</h2>
                )}
                <div>
                    <InfoRow
                        label={t('pages.dsp.packageDetail.customerName')}
                        value={packageDetail?.recipientName || '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.phoneNumber')}
                        value={maskPhoneNumber(packageDetail?.recipientPhone) || '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.address')}
                        value={packageDetail?.recipientAddress || '-'}
                    />
                    {additionalCustomerFields}
                </div>
            </div>
        </div>
    );
};
